import { useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Camera, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const ACCEPT = ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp";
const ALLOWED_MIME = new Set(["image/png", "image/jpeg", "image/webp"]);

/**
 * Profile-picture uploader for the calling user. Surfaces the avatar in the
 * dashboard sidebar (DashboardShell) so every screen confirms identity at a
 * glance.
 *
 * Backend: POST/DELETE /api/user/avatar — stores as a data URI on the user
 * doc, returned by /api/auth/me as `avatar_data_uri`.
 */
export default function ProfilePictureSection() {
  const { user, refresh } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const avatar = user?.avatar_data_uri || null;
  const initials = (user?.full_name || user?.email || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_MIME.has(file.type)) {
      toast.error("Unsupported format. Use PNG, JPG, or WEBP.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File too large — ${(file.size / 1024 / 1024).toFixed(2)} MB. Max 2 MB.`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/user/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile picture updated");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    setBusy(true);
    try {
      await api.delete("/user/avatar");
      toast.success("Profile picture removed");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-sm p-6 mb-6"
      data-testid="profile-picture-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-slate-500">Profile</div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
            Profile picture
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-md">
            Appears in your dashboard sidebar. PNG / JPG / WEBP up to 2 MB. Square images work best — circular crop is applied automatically.
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-5 flex-wrap">
        <div
          className="w-24 h-24 rounded-full bg-slate-900 text-white flex items-center justify-center overflow-hidden ring-2 ring-slate-200"
          data-testid="profile-picture-preview"
        >
          {avatar ? (
            <img src={avatar} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl font-bold tracking-wide" style={{ fontFamily: "Outfit" }}>{initials || "?"}</span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={onFile}
            data-testid="profile-picture-file-input"
          />
          <button
            type="button"
            onClick={onPick}
            disabled={busy}
            className="btn-primary-dark inline-flex items-center gap-2 text-sm disabled:opacity-60"
            data-testid="profile-picture-upload-btn"
          >
            {avatar ? <Camera className="w-4 h-4" /> : <UploadCloud className="w-4 h-4" />}
            {busy ? "Uploading…" : avatar ? "Change picture" : "Upload picture"}
          </button>
          {avatar && (
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1.5 self-start disabled:opacity-60"
              data-testid="profile-picture-remove-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
